import {
    type NodeCfg,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import {
    type DeletableTextWithTypeSelectorCfg,
    UiFlowNodeControl,
    type UiFlowNodeControlConfig,
} from '../../controls/UiFlowNodeControl';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';

export const functionalInputsUiNodeSpec: UiNodeSpec<'functionalInputs'> = {
    canCopy: false,
    canDelete: false,
    suppressNode: true,
    inputs: () => ({}),
    nodeCategory: 'other',
    kindLabel: 'Functional Inputs',
    bypassSocketDefaultDisable: true,
    validator: node => {
        const errors: Record<string, string> = {};
        node.controlValues.inputs.forEach((input, inputIdx) => {
            if (!input.name) {
                errors.inputs = 'One or more input names are empty';
            } else {
                const otherInputs = [
                    ...node.controlValues.inputs.slice(0, inputIdx),
                    ...node.controlValues.inputs.slice(inputIdx + 1),
                ];
                if (otherInputs.find(o => o.name === input.name)) {
                    errors.inputs = 'One or more inputs share a name';
                }
            }
        });
        return Object.keys(errors).length ? errors : true;
    },
    requiredSockets: node => {
        const ret: ReturnType<RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>> = {
            required: [],
            // this conditionallyRequired statement basically says at least one output socket must be wired up
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: [ 'start', ...node.controlValues.inputs.map((_, idx) => `output${idx}`) as `output${number}`[] ],
                },
            ],
        };
        return ret;
    },
    cfg: () => ({
        controlValues: {
            inputs: [],
        },
        id: getUID(),
        kind: 'functionalInputs',
        disabled: false,
    }),
    controls: getNodeCfg => ({
        inputs: ({
            cfg: {
                kind: 'button',
                label: 'Add Input',
                onClick: () => {
                    const controlValues: NodeCfg<'functionalInputs'>['controlValues'] = {
                        ...getNodeCfg().controlValues,
                        inputs: [
                            ...getNodeCfg().controlValues.inputs,
                            {
                                name: '',
                                dataType: 'string',
                            },
                        ],
                    };
                    const newNodeCfg = {
                        ...getNodeCfg(),
                        controlValues,
                    };

                    createNodeSocket({
                        newNodeCfg,
                    });
                },
            },
            index: 1,
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.inputs,
        }),
    }),
    outputs: cfg => {
        const outputs: ReturnType<UiNodeSpec<'functionalInputs'>['outputs']> = {
            start: { socket: 'event', label: 'Start' },
        };
        for (let i = 0; i < cfg.controlValues.inputs.length; i++) {
            const output = cfg.controlValues.inputs[i];
            const controlCfg: DeletableTextWithTypeSelectorCfg<NodeCfg<'functionalInputs'>['controlValues']['inputs'][number]['dataType']> = {
                valuePlaceholder: 'Input Name',
                selectionPlaceholder: 'Input Type',
                kind: 'deletableTextControlWithTypeSelector',
                allowedDataTypes: [ 'string', 'number', 'boolean' ],
                getValue: nodeCfg => {
                    if (nodeCfg.kind !== 'functionalInputs') {
                        return null;
                    }
                    const inputFromCfg = nodeCfg.controlValues.inputs[i];
                    return {
                        newValue: inputFromCfg.name,
                        dataType: inputFromCfg.dataType,
                    };
                },
                onChange: newValue => {
                    const newInputs: NodeCfg<'functionalInputs'>['controlValues']['inputs'] = [
                        ...cfg.controlValues.inputs.slice(0, i),
                        {
                            name: newValue.newValue,
                            dataType: newValue.dataType,
                        },
                        ...cfg.controlValues.inputs.slice(i + 1),
                    ];

                    createNodeSocket({
                        newNodeCfg: {
                            ...cfg,
                            controlValues: {
                                ...cfg.controlValues,
                                inputs: newInputs,
                            },
                        },
                    });
                },
                onDelete () {
                    removeNodeSocket<'functionalInputs'>({
                        direction: 'output',
                            removalMethod: {
                            index: i,
                            prefix: 'output',
                            kind: 'indexedIdentifier',
                        },
                        newNodeCfg: {
                            ...cfg,
                            controlValues: {
                                ...cfg.controlValues,
                                inputs: [
                                    ...cfg.controlValues.inputs.slice(0, i),
                                    ...cfg.controlValues.inputs.slice(i + 1),
                                ],
                            },
                        },
                    });
                },
            };
            const control: UiFlowNodeControl.Opts<unknown> = {
                nodeCfg: cfg,
                value: cfg.controlValues.inputs[i],
                cfg: controlCfg as UiFlowNodeControlConfig & { kind: 'deletableTextControlWithTypeSelector' },
            };
            outputs[`output${i}`] = ({
                socket: output.dataType,
                control,
            });
        }
        return outputs;
    },
};
